import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { AnchoredMenu } from '../ui/AnchoredMenu';
import { ChevronDownIcon } from '../ui/Icons';
import type { Column } from '../DataTable/DataTable';
import type { ExportTableData, ListModuleConfig } from './crudTypes';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return err.response?.data?.message || err.message || fallback;
  }
  return fallback;
}

type ToolbarAction<T extends object> = ListModuleConfig<T>['toolbar']['actions'][number];

export interface ListPageToolbarProps<T extends object> {
  config: ListModuleConfig<T>;
  localizedToolbarActions: ToolbarAction<T>[];
  colors: ThemeColors;
  canEdit: boolean;
  permissionModuleKey?: string;
  enableExport: boolean;
  enableTablePresets: boolean;
  embedded: boolean;
  moduleTitle: string;
  data: T[] | undefined;
  sortedItems: T[];
  localizedAllColumns: Column<T>[];
  showTablePresets: boolean;
  setShowTablePresets: (open: boolean) => void;
  showError: (message: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function ListPageToolbar<T extends object>({
  config,
  localizedToolbarActions,
  colors,
  canEdit,
  permissionModuleKey,
  enableExport,
  enableTablePresets,
  embedded,
  moduleTitle,
  data,
  sortedItems,
  localizedAllColumns,
  showTablePresets: _showTablePresets,
  setShowTablePresets,
  showError,
  t,
}: ListPageToolbarProps<T>) {
  const { t: tCommon } = useTranslation('common');
  const [viewToggleDropdownOpen, setViewToggleDropdownOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const viewToggleDropdownRef = useRef<HTMLDivElement>(null);
  const viewToggleButtonRef = useRef<HTMLButtonElement>(null);
  const viewToggleMenuRef = useRef<HTMLDivElement>(null);
  const [viewToggleDropdownPosition, setViewToggleDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const primaryActions = useMemo(
    () =>
      localizedToolbarActions.filter(
        (action) => action.position === 'beforeTablePresets' || action.variant === 'primary',
      ),
    [localizedToolbarActions],
  );

  const secondaryActions = useMemo(
    () =>
      localizedToolbarActions.filter((action) => {
        if (action.position === 'beforeTablePresets' || action.variant === 'primary') {
          return false;
        }
        if (action.key === 'download' && !enableExport) {
          return false;
        }
        if (permissionModuleKey && !canEdit && action.href === config.module.route.new) {
          return false;
        }
        return true;
      }),
    [localizedToolbarActions, enableExport, permissionModuleKey, canEdit, config.module.route.new],
  );

  const showMoreMenu =
    !embedded &&
    (enableTablePresets || secondaryActions.length > 0);

  const renderActionButton = (action: ToolbarAction<T>, forcePrimary = false) => {
    const iconElement = config.getToolbarIcon(action.icon);
    const showTitle = action.showTitle !== false || forcePrimary;

    const buttonClass = cn(
      'inline-flex items-center justify-center text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
      showTitle ? 'px-3.5 py-2 gap-2' : 'w-9 p-0',
      action.variant === 'primary'
        ? cn(colors.primary, colors.primaryText, 'border-transparent hover:opacity-90')
        : cn(colors.surface, colors.text, colors.border, colors.interactiveHover),
      action.className,
    );

    if (action.href) {
      return (
        <Link key={action.key} to={action.href} className={buttonClass} title={action.title}>
          <span className="text-inherit">{iconElement}</span>
          {showTitle && action.title}
        </Link>
      );
    }

    return (
      <button key={action.key} type="button" onClick={action.onClick} className={buttonClass} title={action.title}>
        <span className="text-inherit">{iconElement}</span>
        {showTitle && action.title}
      </button>
    );
  };

  const exportContext: ExportTableData<T> = {
    sortedData: sortedItems,
    tableColumns: localizedAllColumns,
    entityName: moduleTitle,
    exportFileName: moduleTitle.toLowerCase().replace(/\s+/g, '_'),
    formatDate: config.utils.formatDate,
  };

  return (
    <div className={cn('flex items-center gap-3 flex-shrink-0 overflow-x-auto flex-nowrap rounded-xl border py-1.5 pl-3 pr-5', colors.border, colors.surface)}>
      {config.toolbar.customButton && config.toolbar.customButton.visible !== false && (canEdit || config.toolbar.customButton.href !== config.module.route.new) && (() => {
        const customButton = config.toolbar.customButton!;
        const customClass = cn(
          'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
          customButton.variant === 'primary'
            ? cn(colors.primary, colors.primaryText, 'border-transparent hover:opacity-90')
            : cn(colors.surface, colors.text, colors.border, colors.interactiveHover),
        );
        return (
          <div className="relative flex-shrink-0 inline-block">
            {customButton.href ? (
              <Link to={customButton.href} className={customClass} title={customButton.title || ''}>
                <span className="text-inherit">{config.getToolbarIcon(customButton.icon)}</span>
              </Link>
            ) : customButton.onClick ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await customButton.onClick!();
                  } catch (error: unknown) {
                    console.error('Custom button error:', error);
                    showError(getErrorMessage(error, 'An error occurred'));
                  }
                }}
                className={customClass}
                title={customButton.title || ''}
              >
                <span className="text-inherit">{config.getToolbarIcon(customButton.icon)}</span>
              </button>
            ) : null}
          </div>
        );
      })()}

      {config.toolbar.viewToggle?.enabled && (() => {
        const viewToggle = config.toolbar.viewToggle!;
        return (
          <div key="view-toggle" className="relative flex-shrink-0 inline-block" ref={viewToggleDropdownRef}>
            <button
              ref={viewToggleButtonRef}
              type="button"
              onClick={() => {
                if (!viewToggleDropdownOpen && viewToggleButtonRef.current) {
                  const rect = viewToggleButtonRef.current.getBoundingClientRect();
                  setViewToggleDropdownPosition({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                  });
                  setViewToggleDropdownOpen(true);
                } else {
                  setViewToggleDropdownOpen(false);
                  setViewToggleDropdownPosition(null);
                }
              }}
              className={cn(
                'inline-flex items-center justify-center h-9 min-w-[2.25rem] px-2.5 rounded-lg border transition-colors',
                viewToggleDropdownOpen
                  ? cn(colors.primary, colors.primaryText, 'border-transparent hover:opacity-90')
                  : cn(colors.surface, colors.text, colors.border, colors.interactiveHover),
              )}
              title="View Options"
            >
              <svg className="w-5 h-5 text-inherit shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
              </svg>
              <ChevronDownIcon className={cn('w-4 h-4 ml-1 transition-transform text-inherit', viewToggleDropdownOpen ? 'rotate-180' : '')} />
            </button>

            {viewToggleDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-[90]"
                  onClick={() => {
                    setViewToggleDropdownOpen(false);
                    setViewToggleDropdownPosition(null);
                  }}
                />
                <div
                  ref={viewToggleMenuRef}
                  className={cn('fixed z-[100] min-w-[180px] rounded-lg shadow-xl border overflow-hidden', colors.surface, colors.border)}
                  style={
                    viewToggleDropdownPosition
                      ? { top: `${viewToggleDropdownPosition.top}px`, right: `${viewToggleDropdownPosition.right}px` }
                      : undefined
                  }
                >
                  {viewToggle.views.map((view) => {
                    const viewIcon = config.getToolbarIcon(view.icon);
                    return (
                      <Link
                        key={view.key}
                        to={view.href}
                        onClick={() => {
                          setViewToggleDropdownOpen(false);
                          setViewToggleDropdownPosition(null);
                        }}
                        className={cn('w-full px-3 py-2 text-sm text-left transition-colors flex items-center gap-2', colors.text)}
                      >
                        <span className="text-inherit">{viewIcon}</span>
                        <span>{view.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {primaryActions.map((action) => renderActionButton(action))}

      {showMoreMenu && (
        <div className="relative flex-shrink-0">
          <button
            ref={moreButtonRef}
            type="button"
            onClick={() => setMoreMenuOpen((open) => !open)}
            className={cn(
              'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium transition-colors',
              moreMenuOpen
                ? cn(colors.primary, colors.primaryText, 'border-transparent hover:opacity-90')
                : cn(colors.surface, colors.text, colors.border, colors.interactiveHover),
            )}
          >
            {tCommon('listToolbar.more', { defaultValue: 'More' })}
            <ChevronDownIcon className={cn('w-4 h-4 transition-transform', moreMenuOpen ? 'rotate-180' : '')} />
          </button>

          <AnchoredMenu open={moreMenuOpen} onClose={() => setMoreMenuOpen(false)} anchorRef={moreButtonRef} align="end">
            <div className={cn('min-w-[200px] rounded-lg shadow-xl border py-1', colors.surface, colors.border)}>
              {enableTablePresets && (
                <button
                  type="button"
                  className={cn('w-full px-3 py-2 text-sm text-left flex items-center gap-2', colors.text, colors.interactiveHover)}
                  onClick={() => {
                    setShowTablePresets(true);
                    setMoreMenuOpen(false);
                  }}
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.827 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.827 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.827-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.827-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('table.managePresets')}
                </button>
              )}

              {secondaryActions.map((action) => {
                if (action.dropdown?.length) {
                  return action.dropdown.map((item) => {
                    const itemIcon = item.icon ? config.getToolbarIcon(item.icon) : null;
                    return (
                      <button
                        key={`${action.key}-${item.key}`}
                        type="button"
                        className={cn('w-full px-3 py-2 text-sm text-left flex items-center gap-2', colors.text, colors.interactiveHover)}
                        onClick={async () => {
                          try {
                            if (typeof item.onClick === 'function') {
                              if (item.onClick.length > 0) {
                                await item.onClick(exportContext);
                              } else {
                                await (item.onClick as () => void | Promise<void>)();
                              }
                            }
                            setMoreMenuOpen(false);
                          } catch (error: unknown) {
                            console.error('Action error:', error);
                            showError(getErrorMessage(error, 'An error occurred'));
                            setMoreMenuOpen(false);
                          }
                        }}
                      >
                        {itemIcon && <span className="text-inherit">{itemIcon}</span>}
                        <span>{item.label}</span>
                      </button>
                    );
                  });
                }

                const iconElement = config.getToolbarIcon(action.icon);
                if (action.href) {
                  return (
                    <Link
                      key={action.key}
                      to={action.href}
                      className={cn('w-full px-3 py-2 text-sm text-left flex items-center gap-2', colors.text, colors.interactiveHover)}
                      onClick={() => setMoreMenuOpen(false)}
                    >
                      <span className="text-inherit">{iconElement}</span>
                      <span>{action.title}</span>
                    </Link>
                  );
                }

                return (
                  <button
                    key={action.key}
                    type="button"
                    className={cn('w-full px-3 py-2 text-sm text-left flex items-center gap-2', colors.text, colors.interactiveHover)}
                    onClick={() => {
                      action.onClick?.();
                      setMoreMenuOpen(false);
                    }}
                  >
                    <span className="text-inherit">{iconElement}</span>
                    <span>{action.title}</span>
                  </button>
                );
              })}
            </div>
          </AnchoredMenu>
        </div>
      )}
    </div>
  );
}

export default ListPageToolbar;
